import * as Popover from "@radix-ui/react-popover";
import { BellIcon } from "@radix-ui/react-icons";
import {
  Auth0AccountRole,
  MeetingAdminResponse,
  MeetingLimitedResponse,
  MeetingsService,
  OrgEventKind,
  OrgEventRow,
  OrgEventsService,
} from "client/openapi";
import CountBadge from "components/CountBadge";
import { SessionRequestsDialog } from "components/Dashboard/PeoplePanel/MeetingRequests";
import { profilePath } from "components/Dashboard/PeoplePanel/person";
import { Dialog } from "components/Dialog";
import ResponseDialog from "components/Forms/ResponseDialog";
import MeetingDialog from "components/MeetingDialog";
import moment from "moment";
import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { IconType } from "react-icons";
import {
  LuAlertTriangle,
  LuCalendarCheck,
  LuCalendarPlus,
  LuCalendarX,
  LuCreditCard,
  LuFileText,
  LuLandmark,
} from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import Notifications from "util/notifications";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import { eventCount, useUnread } from "util/UnreadContext";
import "./index.css";

/** An event in the panel, and whether this admin has read it. */
type Item = { event: OrgEventRow; read: boolean };

/** "4:12 pm" today, "Mon" this week, "Sep 12" before that. */
function shortTime(value: string): string {
  const time = moment(value);
  if (time.isSame(moment(), "day")) return time.format("h:mm a");
  if (time.isAfter(moment().subtract(6, "days").startOf("day")))
    return time.format("ddd");
  return time.format("MMM D");
}

/**
 * Rows stay put while the panel is open: anything new goes on top, and an
 * event the admin read stays where it is.
 */
function merge(previous: Item[], fresh: Item[]): Item[] {
  const freshById = new Map(fresh.map((item) => [item.event.id, item]));
  const kept = previous.map((item) => freshById.get(item.event.id) ?? item);
  const known = new Set(previous.map((item) => item.event.id));
  return [...fresh.filter((item) => !known.has(item.event.id)), ...kept];
}

function declined(event: OrgEventRow): boolean {
  return (
    event.kind === OrgEventKind.MEETING_REQUEST_ANSWERED &&
    !!event.detail?.startsWith("declined")
  );
}

function icon(event: OrgEventRow): IconType {
  switch (event.kind) {
    case OrgEventKind.FORM_FILLED:
      return LuFileText;
    case OrgEventKind.PAYMENT_METHOD_ADDED:
      return LuCreditCard;
    case OrgEventKind.CHARGE_FAILED:
      return LuAlertTriangle;
    case OrgEventKind.TUTOR_BANK_ADDED:
      return LuLandmark;
    case OrgEventKind.MEETING_REQUESTED:
      return LuCalendarPlus;
    default:
      return declined(event) ? LuCalendarX : LuCalendarCheck;
  }
}

/**
 * The event's one line: "Ana Lopez filled out Family onboarding", "Dana
 * Whitfield added a card ···· 4242", "A $120.00 charge failed for Maya
 * Whitfield", "Sam Okafor added a payroll bank account", "New meeting
 * request: 'Algebra help' for Mia · Oct 6, 4:00 pm", "Sam Okafor
 * accepted 'Algebra help' for Mia · Oct 6, 4:00 pm", or "Ana Lopez booked
 * Algebra with Jordan Lee for Mia, Oct 6, 4:00 pm".
 */
function line(event: OrgEventRow): ReactNode {
  const who = (
    <span className="font-semibold">{event.person_name || "Someone"}</span>
  );
  const detail = event.detail ?? "";
  switch (event.kind) {
    case OrgEventKind.FORM_FILLED:
      return (
        <>
          {who} filled out {detail || "a form"}
        </>
      );
    case OrgEventKind.PAYMENT_METHOD_ADDED:
      return (
        <>
          {who} added {detail || "a payment method"}
        </>
      );
    case OrgEventKind.CHARGE_FAILED:
      return (
        <>
          A {detail ? `${detail} ` : ""}charge failed for {who}
        </>
      );
    case OrgEventKind.TUTOR_BANK_ADDED:
      return <>{who} added a payroll bank account</>;
    case OrgEventKind.MEETING_REQUESTED:
      return <>New meeting request: {detail}</>;
    default:
      return (
        <>
          {who} {detail}
        </>
      );
  }
}

/**
 * The page a payment or bank event opens: the person's billing or payroll
 * tab, as the People panel's chips do. (A filled-out form, a meeting request,
 * a tutor's answer and a booking made online open in place.)
 */
function target(event: OrgEventRow): string | null {
  const profile =
    event.person_kind && event.person_id
      ? profilePath({ kind: event.person_kind, id: event.person_id })
      : null;
  switch (event.kind) {
    case OrgEventKind.PAYMENT_METHOD_ADDED:
    case OrgEventKind.CHARGE_FAILED:
      return profile && `${profile}#billing`;
    case OrgEventKind.TUTOR_BANK_ADDED:
      return profile && `${profile}#payroll`;
    default:
      return null;
  }
}

/**
 * The bell in the navbar (org admins): what happened in the organization.
 * A form filled out, a payment method added, a charge failed, a tutor's
 * payroll bank added, a meeting request, a tutor's answer to one, or a
 * booking made online that no tutor had to accept: the
 * last 30 days, read ones dimmed, and anything older still unread. Texts
 * and emails live in the Communication view. Opening an event marks it read
 * for this admin only and goes where it points; the dot marks it read or
 * unread again. The badge counts what is unread.
 */
export default function MessagesBell({
  className = "",
}: {
  className?: string;
}) {
  const { currently_selected_role, currently_selected_organization } =
    useContext(OrgRolesAndAccountContext);
  const { summary, setRead, lastChange, polled } = useUnread();
  const navigate = useNavigate();
  const orgId = currently_selected_organization ?? undefined;

  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Item[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [formOpen, setFormOpen] = useState<number | null>(null);
  const [meeting, setMeeting] = useState<
    MeetingAdminResponse | MeetingLimitedResponse
  >();
  // A meeting request's session, open in the requests window.
  const [requestOpen, setRequestOpen] = useState<number | null>(null);
  const closeRequest = useCallback(() => setRequestOpen(null), []);
  const shownPolled = useRef(polled);

  const total = eventCount(summary);

  const load = useCallback(
    async (fresh: boolean) => {
      if (!orgId) return;
      try {
        const events = await OrgEventsService.getOrgEvents({ orgId });
        const next = events.map((event) => ({ event, read: !!event.read }));
        setItems((previous) =>
          fresh || !previous ? next : merge(previous, next)
        );
        setFailed(false);
      } catch {
        if (fresh) setFailed(true);
      }
    },
    [orgId]
  );

  // A fresh list each time the panel opens.
  useEffect(() => {
    if (!open) return;
    setItems(null);
    load(true);
  }, [open, load]);

  // Something new arrived while the panel is open: add it on top.
  useEffect(() => {
    if (open && polled !== shownPolled.current) load(false);
    shownPolled.current = polled;
  }, [open, polled, load]);

  // Read marks show at once.
  useEffect(() => {
    if (!lastChange) return;
    const ids = new Set(lastChange.eventIds);
    setItems(
      (previous) =>
        previous?.map((item) =>
          ids.has(item.event.id) ? { ...item, read: lastChange.read } : item
        ) ?? null
    );
  }, [lastChange]);

  if (
    currently_selected_role !== Auth0AccountRole.ORG_ADMIN &&
    currently_selected_role !== Auth0AccountRole.TADPOLE_ADMIN
  ) {
    return null;
  }

  async function mark(eventIds: number[], read: boolean) {
    try {
      await setRead({ eventIds }, read);
    } catch {
      Notifications.error("That could not be saved. Try again.");
    }
  }

  // Opening an event marks it read for this admin, then goes where it points.
  function openItem(item: Item) {
    const { event } = item;
    if (!item.read) mark([event.id], true);
    setOpen(false);
    if (event.form_send_id) {
      setFormOpen(event.form_send_id);
      return;
    }
    // A tutor's answer or a booking made online: that meeting, in the
    // calendar's dialog, as the People panel's next-meeting chip opens it.
    if (
      (event.kind === OrgEventKind.MEETING_REQUEST_ANSWERED ||
        event.kind === OrgEventKind.MEETING_BOOKED) &&
      event.meeting_id
    ) {
      MeetingsService.getMeetingById({ meetingId: event.meeting_id })
        .then(setMeeting)
        .catch(() => Notifications.error("Could not open the meeting."));
      return;
    }
    // A request (or an answer whose meeting is gone): its session's requests
    // in the requests window, which says when they are all answered.
    if (event.session_id) {
      setRequestOpen(event.session_id);
      return;
    }
    const to = target(event);
    if (to) navigate(to);
  }

  // Every event this admin hasn't read, listed or not; Undo marks the same
  // ones unread again.
  async function markAllRead() {
    try {
      const result = await setRead({ allEvents: true }, true);
      const marked = result?.event_ids ?? [];
      if (marked.length) {
        Notifications.undoable(`Marked ${marked.length} read.`, () =>
          mark(marked, false)
        );
      }
      load(false);
    } catch {
      Notifications.error("That could not be saved. Try again.");
    }
  }

  const anyUnread = (items ?? []).some((item) => !item.read);
  const caughtUp = !!items && !anyUnread && total === 0;

  return (
    <>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            type="button"
            className={`messages-bell ${className}`}
            aria-label={
              total ? `Notifications, ${total} unread` : "Notifications"
            }
            title="Notifications"
          >
            <BellIcon width="20" height="20" />
            <CountBadge count={total} color="bg-blue-500" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className="messages-bell__panel"
            align="end"
            sideOffset={8}
            collisionPadding={16}
          >
            <div className="messages-bell__header">
              <h2 className="text-base font-semibold text-gray-900">
                Notifications
              </h2>
              {(anyUnread || total > 0) && (
                <button
                  type="button"
                  className="messages-bell__link"
                  onClick={markAllRead}
                >
                  Mark all read
                </button>
              )}
            </div>

            <div className="messages-bell__list">
              {failed && (
                <div className="p-5 text-sm text-gray-600">
                  Couldn't load notifications.{" "}
                  <button
                    type="button"
                    className="messages-bell__link"
                    onClick={() => load(true)}
                  >
                    Try again
                  </button>
                </div>
              )}
              {!failed && !items && (
                <div className="space-y-3 p-5">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="animate-pulse space-y-2">
                      <div className="h-4 w-3/4 rounded bg-gray-200" />
                      <div className="h-3 w-1/3 rounded bg-gray-100" />
                    </div>
                  ))}
                </div>
              )}
              {caughtUp && items.length === 0 && (
                <div className="px-5 py-10 text-center">
                  <BellIcon
                    className="mx-auto mb-3 text-gray-300"
                    width="24"
                    height="24"
                  />
                  <p className="text-sm font-medium text-gray-900">
                    You're all caught up.
                  </p>
                  <p className="text-sm text-gray-500">
                    Filled-out forms, payment updates and meeting requests show
                    up here.
                  </p>
                </div>
              )}
              {caughtUp && items.length > 0 && (
                <p className="messages-bell__caught-up">
                  You're all caught up.
                </p>
              )}
              {items?.map((item) => (
                <EventRow
                  key={item.event.id}
                  item={item}
                  onOpen={() => openItem(item)}
                  onToggleRead={() => mark([item.event.id], !item.read)}
                />
              ))}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      <Dialog
        open={formOpen !== null}
        onOpenChange={(isOpen) => !isOpen && setFormOpen(null)}
      >
        {formOpen !== null && <ResponseDialog responseId={formOpen} />}
      </Dialog>

      <Dialog
        open={!!meeting}
        onOpenChange={(isOpen) => !isOpen && setMeeting(undefined)}
      >
        {meeting && (
          <MeetingDialog
            role={Auth0AccountRole.ORG_ADMIN}
            event={meeting}
            setEvents={() => Promise.resolve()}
          />
        )}
      </Dialog>

      {orgId && requestOpen !== null && (
        <SessionRequestsDialog
          orgId={orgId}
          sessionId={requestOpen}
          onClose={closeRequest}
        />
      )}
    </>
  );
}

/** One event: the dot, its icon and line, and when. */
function EventRow({
  item,
  onOpen,
  onToggleRead,
}: {
  item: Item;
  onOpen: () => void;
  onToggleRead: () => void;
}) {
  const { event, read } = item;
  const Icon = icon(event);
  const alert = event.kind === OrgEventKind.CHARGE_FAILED || declined(event);
  return (
    <div className="messages-bell__row">
      <button
        type="button"
        className="messages-bell__dot-button"
        onClick={onToggleRead}
        title={read ? "Mark unread" : "Mark read"}
        aria-label={read ? "Mark unread" : "Mark read"}
      >
        <span
          className={`messages-bell__dot ${
            read ? "" : "messages-bell__dot--unread"
          }`}
        />
      </button>
      <button
        type="button"
        className="messages-bell__row-main"
        onClick={onOpen}
      >
        <Icon
          className={`messages-bell__icon ${
            alert ? "messages-bell__icon--alert" : ""
          }`}
          aria-hidden
        />
        <span
          className={`min-w-0 text-sm leading-5 ${
            read ? "text-gray-500" : "text-gray-900"
          }`}
        >
          {line(event)}
        </span>
      </button>
      <span className="messages-bell__side text-xs text-gray-400">
        {shortTime(event.created_at)}
      </span>
    </div>
  );
}
